import { Bugfender } from '@bugfender/sdk';
import {
  DefaultError,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { useBoolean } from 'usehooks-ts';

import { useUpdateUser } from './useUpdateUser';

export function useMutationWithUserData<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>({
  onFinished,
  onError,
  ...options
}: Omit<
  UseMutationOptions<TData, TError, TVariables, TContext>,
  'onSuccess'
> & {
  onFinished?: UseMutationOptions<
    TData,
    TError,
    TVariables,
    TContext
  >['onSuccess'];
}): UseMutationResult<TData, TError, TVariables, TContext> {
  const { value: isRefreshing, setValue: setRefreshing } = useBoolean(false);
  const updateUser = useUpdateUser();

  const mutation = useMutation({
    ...options,
    onSuccess: async (...args) => {
      try {
        await updateUser();
      } catch (e) {
        Bugfender.error(e);
      } finally {
        setRefreshing(false);
        onFinished?.(...args);
      }
    },
    onError: (...args) => {
      setRefreshing(false);
      onError?.(...args);
    },
  });

  const mutate: typeof mutation.mutate = (...params) => {
    setRefreshing(true);
    return mutation.mutate(...params);
  };

  return {
    ...mutation,
    mutate,
    isPending: isRefreshing || mutation.isPending,
  } as UseMutationResult<TData, TError, TVariables, TContext>;
}
